/**
 * shadcn/ui predefined color themes.
 *
 * Each preset ships a full neutral scale (background, card, muted, border, …)
 * for both light and dark modes, plus a default primary hex. Runtime override
 * of `--primary` via BrandThemeProvider still wins, so picking a preset seeds
 * sensible defaults without locking the accent.
 *
 * Values sourced from the official shadcn registry (oklch format).
 */

export type ThemeMode = 'light' | 'dark' | 'system';

export type ThemePresetKey =
  | 'neutral'
  | 'zinc'
  | 'slate'
  | 'stone'
  | 'gray'
  | 'red'
  | 'rose'
  | 'orange'
  | 'green'
  | 'blue'
  | 'yellow'
  | 'violet';

export type PresetScaleToken =
  | 'background'
  | 'foreground'
  | 'card'
  | 'card-foreground'
  | 'popover'
  | 'popover-foreground'
  | 'secondary'
  | 'secondary-foreground'
  | 'muted'
  | 'muted-foreground'
  | 'accent'
  | 'accent-foreground'
  | 'destructive'
  | 'border'
  | 'input'
  | 'ring'
  | 'chart-1'
  | 'chart-2'
  | 'chart-3'
  | 'chart-4'
  | 'chart-5'
  | 'sidebar'
  | 'sidebar-foreground'
  | 'sidebar-accent'
  | 'sidebar-accent-foreground'
  | 'sidebar-border'
  | 'sidebar-ring';

export type PresetScale = Record<PresetScaleToken, string>;

export interface ThemePreset {
  key: ThemePresetKey;
  label: string;
  /** Seed color shown as swatch + used as default primary when preset is applied. */
  primaryHex: string;
  light: PresetScale;
  dark: PresetScale;
}

/* ------------------------------------------------------------------ */
/*  Neutral base scales (reused across presets)                        */
/* ------------------------------------------------------------------ */

const NEUTRAL_LIGHT: PresetScale = {
  background: 'oklch(1 0 0)',
  foreground: 'oklch(0.145 0 0)',
  card: 'oklch(1 0 0)',
  'card-foreground': 'oklch(0.145 0 0)',
  popover: 'oklch(1 0 0)',
  'popover-foreground': 'oklch(0.145 0 0)',
  secondary: 'oklch(0.97 0 0)',
  'secondary-foreground': 'oklch(0.205 0 0)',
  muted: 'oklch(0.97 0 0)',
  'muted-foreground': 'oklch(0.556 0 0)',
  accent: 'oklch(0.97 0 0)',
  'accent-foreground': 'oklch(0.205 0 0)',
  destructive: 'oklch(0.577 0.245 27.325)',
  border: 'oklch(0.922 0 0)',
  input: 'oklch(0.922 0 0)',
  ring: 'oklch(0.708 0 0)',
  'chart-1': 'oklch(0.646 0.222 41.116)',
  'chart-2': 'oklch(0.6 0.118 184.704)',
  'chart-3': 'oklch(0.398 0.07 227.392)',
  'chart-4': 'oklch(0.828 0.189 84.429)',
  'chart-5': 'oklch(0.769 0.188 70.08)',
  sidebar: 'oklch(0.985 0 0)',
  'sidebar-foreground': 'oklch(0.145 0 0)',
  'sidebar-accent': 'oklch(0.97 0 0)',
  'sidebar-accent-foreground': 'oklch(0.205 0 0)',
  'sidebar-border': 'oklch(0.922 0 0)',
  'sidebar-ring': 'oklch(0.708 0 0)',
};

const NEUTRAL_DARK: PresetScale = {
  background: 'oklch(0.145 0 0)',
  foreground: 'oklch(0.985 0 0)',
  card: 'oklch(0.205 0 0)',
  'card-foreground': 'oklch(0.985 0 0)',
  popover: 'oklch(0.205 0 0)',
  'popover-foreground': 'oklch(0.985 0 0)',
  secondary: 'oklch(0.269 0 0)',
  'secondary-foreground': 'oklch(0.985 0 0)',
  muted: 'oklch(0.269 0 0)',
  'muted-foreground': 'oklch(0.708 0 0)',
  accent: 'oklch(0.269 0 0)',
  'accent-foreground': 'oklch(0.985 0 0)',
  destructive: 'oklch(0.704 0.191 22.216)',
  border: 'oklch(1 0 0 / 10%)',
  input: 'oklch(1 0 0 / 15%)',
  ring: 'oklch(0.556 0 0)',
  'chart-1': 'oklch(0.488 0.243 264.376)',
  'chart-2': 'oklch(0.696 0.17 162.48)',
  'chart-3': 'oklch(0.769 0.188 70.08)',
  'chart-4': 'oklch(0.627 0.265 303.9)',
  'chart-5': 'oklch(0.645 0.246 16.439)',
  sidebar: 'oklch(0.205 0 0)',
  'sidebar-foreground': 'oklch(0.985 0 0)',
  'sidebar-accent': 'oklch(0.269 0 0)',
  'sidebar-accent-foreground': 'oklch(0.985 0 0)',
  'sidebar-border': 'oklch(1 0 0 / 10%)',
  'sidebar-ring': 'oklch(0.556 0 0)',
};

const ZINC_LIGHT: PresetScale = {
  ...NEUTRAL_LIGHT,
  foreground: 'oklch(0.141 0.005 285.823)',
  'card-foreground': 'oklch(0.141 0.005 285.823)',
  'popover-foreground': 'oklch(0.141 0.005 285.823)',
  secondary: 'oklch(0.967 0.001 286.375)',
  'secondary-foreground': 'oklch(0.21 0.006 285.885)',
  muted: 'oklch(0.967 0.001 286.375)',
  'muted-foreground': 'oklch(0.552 0.016 285.938)',
  accent: 'oklch(0.967 0.001 286.375)',
  'accent-foreground': 'oklch(0.21 0.006 285.885)',
  border: 'oklch(0.92 0.004 286.32)',
  input: 'oklch(0.92 0.004 286.32)',
  ring: 'oklch(0.705 0.015 286.067)',
  sidebar: 'oklch(0.985 0 0)',
  'sidebar-foreground': 'oklch(0.141 0.005 285.823)',
  'sidebar-accent': 'oklch(0.967 0.001 286.375)',
  'sidebar-accent-foreground': 'oklch(0.21 0.006 285.885)',
  'sidebar-border': 'oklch(0.92 0.004 286.32)',
  'sidebar-ring': 'oklch(0.705 0.015 286.067)',
};

const ZINC_DARK: PresetScale = {
  ...NEUTRAL_DARK,
  background: 'oklch(0.141 0.005 285.823)',
  card: 'oklch(0.21 0.006 285.885)',
  popover: 'oklch(0.21 0.006 285.885)',
  secondary: 'oklch(0.274 0.006 286.033)',
  muted: 'oklch(0.274 0.006 286.033)',
  'muted-foreground': 'oklch(0.705 0.015 286.067)',
  accent: 'oklch(0.274 0.006 286.033)',
  sidebar: 'oklch(0.21 0.006 285.885)',
  'sidebar-accent': 'oklch(0.274 0.006 286.033)',
};

const SLATE_LIGHT: PresetScale = {
  ...NEUTRAL_LIGHT,
  foreground: 'oklch(0.129 0.042 264.695)',
  'card-foreground': 'oklch(0.129 0.042 264.695)',
  'popover-foreground': 'oklch(0.129 0.042 264.695)',
  secondary: 'oklch(0.968 0.007 247.896)',
  'secondary-foreground': 'oklch(0.208 0.042 265.755)',
  muted: 'oklch(0.968 0.007 247.896)',
  'muted-foreground': 'oklch(0.554 0.046 257.417)',
  accent: 'oklch(0.968 0.007 247.896)',
  'accent-foreground': 'oklch(0.208 0.042 265.755)',
  border: 'oklch(0.929 0.013 255.508)',
  input: 'oklch(0.929 0.013 255.508)',
  ring: 'oklch(0.704 0.04 256.788)',
  sidebar: 'oklch(0.984 0.003 247.858)',
  'sidebar-foreground': 'oklch(0.129 0.042 264.695)',
  'sidebar-accent': 'oklch(0.968 0.007 247.896)',
  'sidebar-accent-foreground': 'oklch(0.208 0.042 265.755)',
  'sidebar-border': 'oklch(0.929 0.013 255.508)',
  'sidebar-ring': 'oklch(0.704 0.04 256.788)',
};

const SLATE_DARK: PresetScale = {
  ...NEUTRAL_DARK,
  background: 'oklch(0.129 0.042 264.695)',
  foreground: 'oklch(0.984 0.003 247.858)',
  card: 'oklch(0.208 0.042 265.755)',
  'card-foreground': 'oklch(0.984 0.003 247.858)',
  popover: 'oklch(0.208 0.042 265.755)',
  'popover-foreground': 'oklch(0.984 0.003 247.858)',
  secondary: 'oklch(0.279 0.041 260.031)',
  'secondary-foreground': 'oklch(0.984 0.003 247.858)',
  muted: 'oklch(0.279 0.041 260.031)',
  'muted-foreground': 'oklch(0.704 0.04 256.788)',
  accent: 'oklch(0.279 0.041 260.031)',
  'accent-foreground': 'oklch(0.984 0.003 247.858)',
  ring: 'oklch(0.551 0.027 264.364)',
  sidebar: 'oklch(0.208 0.042 265.755)',
  'sidebar-foreground': 'oklch(0.984 0.003 247.858)',
  'sidebar-accent': 'oklch(0.279 0.041 260.031)',
  'sidebar-accent-foreground': 'oklch(0.984 0.003 247.858)',
};

const GESTY_LIGHT: PresetScale = {
  ...SLATE_LIGHT,
  background: 'oklch(0.985 0.006 255)',
  foreground: 'oklch(0.145 0.018 260)',
  'card-foreground': 'oklch(0.145 0.018 260)',
  'popover-foreground': 'oklch(0.145 0.018 260)',
  secondary: 'oklch(1 0 0)',
  'secondary-foreground': 'oklch(0.145 0.018 260)',
  muted: 'oklch(0.963 0.008 252)',
  'muted-foreground': 'oklch(0.47 0.025 260)',
  accent: 'oklch(0.955 0.026 260)',
  'accent-foreground': 'oklch(0.52 0.22 263.4)',
  border: 'oklch(0.918 0.012 252)',
  input: 'oklch(0.918 0.012 252)',
  ring: 'oklch(0.576 0.232 263.4)',
  'chart-1': 'oklch(0.576 0.232 263.4)',
  'chart-2': 'oklch(0.69 0.16 196)',
  'chart-3': 'oklch(0.72 0.16 151)',
  'chart-4': 'oklch(0.73 0.18 75)',
  'chart-5': 'oklch(0.64 0.2 310)',
  sidebar: 'oklch(1 0 0)',
  'sidebar-foreground': 'oklch(0.145 0.018 260)',
  'sidebar-accent': 'oklch(0.955 0.026 260)',
  'sidebar-accent-foreground': 'oklch(0.52 0.22 263.4)',
  'sidebar-border': 'oklch(0.918 0.012 252)',
  'sidebar-ring': 'oklch(0.576 0.232 263.4)',
};

const GESTY_DARK: PresetScale = {
  ...SLATE_DARK,
  background: 'oklch(0.145 0.025 260)',
  card: 'oklch(0.205 0.03 260)',
  popover: 'oklch(0.205 0.03 260)',
  secondary: 'oklch(0.25 0.028 260)',
  muted: 'oklch(0.25 0.028 260)',
  'muted-foreground': 'oklch(0.72 0.025 260)',
  accent: 'oklch(0.28 0.045 260)',
  'accent-foreground': 'oklch(0.88 0.08 263.4)',
  ring: 'oklch(0.68 0.19 263.4)',
  'chart-1': 'oklch(0.68 0.19 263.4)',
  sidebar: 'oklch(0.18 0.028 260)',
  'sidebar-accent': 'oklch(0.28 0.045 260)',
  'sidebar-accent-foreground': 'oklch(0.88 0.08 263.4)',
  'sidebar-ring': 'oklch(0.68 0.19 263.4)',
};

const STONE_LIGHT: PresetScale = {
  ...NEUTRAL_LIGHT,
  foreground: 'oklch(0.147 0.004 49.25)',
  'card-foreground': 'oklch(0.147 0.004 49.25)',
  'popover-foreground': 'oklch(0.147 0.004 49.25)',
  secondary: 'oklch(0.97 0.001 106.424)',
  'secondary-foreground': 'oklch(0.216 0.006 56.043)',
  muted: 'oklch(0.97 0.001 106.424)',
  'muted-foreground': 'oklch(0.553 0.013 58.071)',
  accent: 'oklch(0.97 0.001 106.424)',
  'accent-foreground': 'oklch(0.216 0.006 56.043)',
  border: 'oklch(0.923 0.003 48.717)',
  input: 'oklch(0.923 0.003 48.717)',
  ring: 'oklch(0.709 0.01 56.259)',
  sidebar: 'oklch(0.985 0.001 106.423)',
  'sidebar-foreground': 'oklch(0.147 0.004 49.25)',
  'sidebar-accent': 'oklch(0.97 0.001 106.424)',
  'sidebar-accent-foreground': 'oklch(0.216 0.006 56.043)',
  'sidebar-border': 'oklch(0.923 0.003 48.717)',
  'sidebar-ring': 'oklch(0.709 0.01 56.259)',
};

const STONE_DARK: PresetScale = {
  ...NEUTRAL_DARK,
  background: 'oklch(0.147 0.004 49.25)',
  card: 'oklch(0.216 0.006 56.043)',
  popover: 'oklch(0.216 0.006 56.043)',
  secondary: 'oklch(0.268 0.007 34.298)',
  muted: 'oklch(0.268 0.007 34.298)',
  'muted-foreground': 'oklch(0.709 0.01 56.259)',
  accent: 'oklch(0.268 0.007 34.298)',
  ring: 'oklch(0.553 0.013 58.071)',
  sidebar: 'oklch(0.216 0.006 56.043)',
  'sidebar-accent': 'oklch(0.268 0.007 34.298)',
};

const GRAY_LIGHT: PresetScale = {
  ...NEUTRAL_LIGHT,
  foreground: 'oklch(0.13 0.028 261.692)',
  'card-foreground': 'oklch(0.13 0.028 261.692)',
  'popover-foreground': 'oklch(0.13 0.028 261.692)',
  secondary: 'oklch(0.967 0.003 264.542)',
  'secondary-foreground': 'oklch(0.21 0.034 264.665)',
  muted: 'oklch(0.967 0.003 264.542)',
  'muted-foreground': 'oklch(0.551 0.027 264.364)',
  accent: 'oklch(0.967 0.003 264.542)',
  'accent-foreground': 'oklch(0.21 0.034 264.665)',
  border: 'oklch(0.928 0.006 264.531)',
  input: 'oklch(0.928 0.006 264.531)',
  ring: 'oklch(0.707 0.022 261.325)',
  sidebar: 'oklch(0.985 0.002 247.839)',
  'sidebar-foreground': 'oklch(0.13 0.028 261.692)',
  'sidebar-accent': 'oklch(0.967 0.003 264.542)',
  'sidebar-accent-foreground': 'oklch(0.21 0.034 264.665)',
  'sidebar-border': 'oklch(0.928 0.006 264.531)',
  'sidebar-ring': 'oklch(0.707 0.022 261.325)',
};

const GRAY_DARK: PresetScale = {
  ...NEUTRAL_DARK,
  background: 'oklch(0.13 0.028 261.692)',
  card: 'oklch(0.21 0.034 264.665)',
  popover: 'oklch(0.21 0.034 264.665)',
  secondary: 'oklch(0.278 0.033 256.848)',
  muted: 'oklch(0.278 0.033 256.848)',
  'muted-foreground': 'oklch(0.707 0.022 261.325)',
  accent: 'oklch(0.278 0.033 256.848)',
  ring: 'oklch(0.446 0.03 256.802)',
  sidebar: 'oklch(0.21 0.034 264.665)',
  'sidebar-accent': 'oklch(0.278 0.033 256.848)',
};

/* ------------------------------------------------------------------ */
/*  Presets (combine neutral scale + default primary hex)              */
/* ------------------------------------------------------------------ */

export const THEME_PRESETS: Record<ThemePresetKey, ThemePreset> = {
  neutral: { key: 'neutral', label: 'Neutral', primaryHex: '#1f6fff', light: GESTY_LIGHT, dark: GESTY_DARK },
  zinc:    { key: 'zinc',    label: 'Zinc',    primaryHex: '#18181b', light: ZINC_LIGHT,    dark: ZINC_DARK    },
  slate:   { key: 'slate',   label: 'Slate',   primaryHex: '#1B2A4A', light: SLATE_LIGHT,   dark: SLATE_DARK   },
  stone:   { key: 'stone',   label: 'Stone',   primaryHex: '#1c1917', light: STONE_LIGHT,   dark: STONE_DARK   },
  gray:    { key: 'gray',    label: 'Gray',    primaryHex: '#111827', light: GRAY_LIGHT,    dark: GRAY_DARK    },
  red:     { key: 'red',     label: 'Red',     primaryHex: '#dc2626', light: ZINC_LIGHT,    dark: ZINC_DARK    },
  rose:    { key: 'rose',    label: 'Rose',    primaryHex: '#e11d48', light: ZINC_LIGHT,    dark: ZINC_DARK    },
  orange:  { key: 'orange',  label: 'Orange',  primaryHex: '#ea580c', light: ZINC_LIGHT,    dark: ZINC_DARK    },
  green:   { key: 'green',   label: 'Green',   primaryHex: '#16a34a', light: ZINC_LIGHT,    dark: ZINC_DARK    },
  blue:    { key: 'blue',    label: 'Blue',    primaryHex: '#1f6fff', light: SLATE_LIGHT,   dark: SLATE_DARK   },
  yellow:  { key: 'yellow',  label: 'Yellow',  primaryHex: '#eab308', light: ZINC_LIGHT,    dark: ZINC_DARK    },
  violet:  { key: 'violet',  label: 'Violet',  primaryHex: '#7c3aed', light: ZINC_LIGHT,    dark: ZINC_DARK    },
};

export const DEFAULT_PRESET: ThemePresetKey = 'neutral';
export const DEFAULT_MODE: ThemeMode = 'light';

/**
 * Build the CSS block that applies a preset scale under a given selector.
 * Excludes `--primary` and `--primary-foreground` — those are owned by the
 * brand primary color system and injected separately.
 */
/**
 * Tokens we deliberately want the consumer's index.css to govern in
 * light mode, so the body / sidebar can carry a brand-tinted color via
 * color-mix on --primary regardless of which preset is selected.
 * Dark mode keeps the preset's neutral values because tinting near-
 * black surfaces with primary turns them murky.
 */
const LIGHT_MODE_OVERRIDE_TOKENS = new Set([
  'background',
  'sidebar',
  'sidebar-accent',
  'sidebar-accent-foreground',
  'sidebar-border',
  'sidebar-ring',
]);

export function buildPresetCss(selector: string, scale: PresetScale): string {
  const skipBrandTokens = selector === ':root';
  const lines: string[] = [];
  for (const [token, value] of Object.entries(scale)) {
    if (skipBrandTokens && LIGHT_MODE_OVERRIDE_TOKENS.has(token)) continue;
    lines.push(`  --${token}: ${value};`);
  }
  return `${selector} {\n${lines.join('\n')}\n}`;
}

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function isThemePresetKey(value: unknown): value is ThemePresetKey {
  return typeof value === 'string' && value in THEME_PRESETS;
}
